import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Rnd } from 'react-rnd';
import { makeStyles } from 'tss-react/mui';
import useRtcStats from '../hooks/useRtcStats';

// YouTube "stats for nerds"-style panel. FREE-FLOATING: portaled to <body> and
// wrapped in react-rnd so it's a draggable window that escapes the video tile's
// overflow:hidden — drag it anywhere on screen, over other tiles or the chat.
// One panel per tile (tile-local `statsOpen` state), opened near its tile via
// `anchorRect`. Rows render in YouTube's order with honest WebRTC labels; rows
// with no RTC equivalent (Color, normalized loudness) are dropped / repurposed
// in the hook, not shown here.
const PANEL_W = 320;

const useStyles = makeStyles()((theme) => ({
  // The Rnd child fills its frame; the visible card is this box.
  panel: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    overflowY: 'auto',
    backgroundColor: 'rgba(0, 0, 0, 0.86)',
    color: '#eaeaea',
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: 11,
    lineHeight: 1.45,
    borderRadius: 6,
    border: `1px solid ${theme.palette.custom ? theme.palette.custom.line : 'rgba(255,255,255,0.15)'}`,
    boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
  },
  // Drag handle (the whole header bar grabs the window).
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    fontWeight: 700,
    color: theme.palette.primary.main,
    cursor: 'move',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    position: 'sticky',
    top: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.92)',
    userSelect: 'none',
  },
  titleWrap: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  subtitle: {
    fontWeight: 400,
    fontSize: 10,
    color: '#9aa0a6',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  close: {
    cursor: 'pointer',
    color: '#aaa',
    padding: '0 4px',
    fontSize: 13,
    flexShrink: 0,
    '&:hover': { color: '#fff' },
  },
  body: {
    padding: theme.spacing(0.75, 1),
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    whiteSpace: 'nowrap',
  },
  label: { color: '#9aa0a6', flexShrink: 0 },
  value: { color: '#eaeaea', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis' },
  debug: {
    marginTop: theme.spacing(0.5),
    color: '#9aa0a6',
    whiteSpace: 'normal', // wrap like YouTube's "mystery text"
    wordBreak: 'break-word',
  },
  note: { color: '#9aa0a6' },
}));

// Ordered rows: [label, snapshotKey]. Multi-value rows are pre-joined in the hook.
const ROWS = [
  ['Consumer ID', 'consumerId'],
  ['Producer ID', 'producerId'],
  ['Viewport / Frames', 'framesViewport'],
  ['Current / Optimal Res', 'res'],
  ['Volume / Audio level', 'volAudio'],
  ['Video codec', 'videoCodec'],
  ['Scalability', 'scalability'],
  ['Codecs', 'codecs'],
  ['Connection Speed', 'connectionSpeed'],
  ['Network Activity', 'networkActivity'],
  ['Buffer Health', 'bufferHealth'],
  ['Packet Loss', 'packetLoss'],
];

// Clamp a desired {x,y} so the window is always fully on-screen.
function clampToViewport(x, y) {
  const margin = 8;
  const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const h = typeof window !== 'undefined' ? window.innerHeight : 720;
  return {
    x: Math.max(margin, Math.min(x, w - PANEL_W - margin)),
    y: Math.max(margin, Math.min(y, h - 120)),
  };
}

// The open position for a fresh panel: the position the user last dragged it to
// (savedPos — restored verbatim, no nudge) if present; otherwise just inside the
// tile's top-left (anchorRect + an 8px nudge); otherwise a sensible default.
function openPosition(savedPos, anchorRect) {
  if (savedPos) return clampToViewport(savedPos.x, savedPos.y);
  if (anchorRect) return clampToViewport(anchorRect.left + 8, anchorRect.top + 8);
  return clampToViewport(80, 80);
}

const StatsForNerds = ({
  producerId,
  isSelf,
  viewEl,
  username,
  userId,
  anchorRect,
  savedPos,
  onPositionChange,
  onClose,
}) => {
  const { classes } = useStyles();
  const s = useRtcStats({ producerId, isSelf, viewEl, enabled: true, userId });

  // Open position (per mount): the dragged position (savedPos, lifted to App.js
  // so it survives the tile's unmount/remount on pop-out/in) restored verbatim,
  // else anchored near the tile. Clamped on-screen.
  const start = useMemo(() => openPosition(savedPos, anchorRect), [savedPos, anchorRect]);

  // Pre-compose the "a / b" rows so the table stays a flat label→value map.
  const view = s && !s.unavailable
    ? {
        ...s,
        framesViewport: `${s.viewport} / ${s.frames}`,
        res: `${s.currentRes} / ${s.optimalRes}`,
        volAudio: `${s.volume} / ${s.audioLevel}`,
      }
    : null;

  // React events bubble through the PORTAL'S React tree, not the DOM tree. Since
  // this panel is rendered inside <VideoParticipantTile> (which, when popped out,
  // is wrapped in App.js's <Rnd>), a mousedown in the panel would otherwise
  // bubble to that tile's <Rnd> and drag the whole video window. We stop the
  // drag-relevant events at a wrapper placed OUTSIDE the panel's own <Rnd>: the
  // panel's react-draggable listener lives inside this wrapper so it fires first
  // (the panel still drags), and the stopper then prevents the leak to the
  // tile's <Rnd>. onClick is stopped too so the ✕ / clicks don't reach the tile.
  const stop = (e) => e.stopPropagation();
  const node = (
    // Zero-size FIXED wrapper. It must not become the Rnd's positioning offset
    // parent (a normal-flow wrapper at the bottom of <body> would shift the
    // Rnd down by the full page height) and must not occupy layout space — so
    // it's position:fixed at the origin with 0×0 size. The Rnd inside is also
    // position:fixed, so it stays viewport-relative; the wrapper exists only to
    // catch the panel's drag/click events and stopPropagation them before they
    // bubble (through the portal's React tree) to a popped-out tile's <Rnd>.
    <div
      style={{ position: 'fixed', top: 0, left: 0, width: 0, height: 0, zIndex: 1400 }}
      onMouseDown={stop}
      onTouchStart={stop}
      onClick={stop}
    >
    <Rnd
      default={{ x: start.x, y: start.y, width: PANEL_W, height: 'auto' }}
      minWidth={240}
      minHeight={80}
      bounds="window"
      style={{ position: 'fixed', zIndex: 1400 }} // above MUI AppBar/drawers
      dragHandleClassName={classes.header}
      enableResizing={{ bottom: true, right: true, bottomRight: true }}
      // Lift the position up so it survives this panel's unmount/remount when the
      // tile is popped out/in (otherwise the fresh instance snaps back to anchor).
      onDragStop={(e, d) => onPositionChange && onPositionChange({ x: d.x, y: d.y })}
      onResizeStop={(e, dir, ref, delta, pos) =>
        onPositionChange && onPositionChange({ x: pos.x, y: pos.y })
      }
    >
      <div className={classes.panel}>
        <div className={classes.header}>
          <div className={classes.titleWrap}>
            <span>Stats for nerds</span>
            <span className={classes.subtitle}>
              {isSelf ? 'You' : username || 'stream'}
            </span>
          </div>
          <span className={classes.close} title="Close" onClick={onClose}>
            ✕
          </span>
        </div>

        <div className={classes.body}>
          {!s ? (
            <div className={classes.note}>Collecting…</div>
          ) : s.unavailable ? (
            <div className={classes.note}>No active stream to measure.</div>
          ) : (
            <>
              {ROWS.map(([label, key]) => (
                <div className={classes.row} key={key}>
                  <span className={classes.label}>{label}</span>
                  <span className={classes.value} title={view[key]}>
                    {view[key]}
                  </span>
                </div>
              ))}
              {view.debug ? <div className={classes.debug}>{view.debug}</div> : null}
              <div className={classes.debug}>{view.date}</div>
            </>
          )}
        </div>
      </div>
    </Rnd>
    </div>
  );

  // Portal to <body> so the window escapes the tile's overflow:hidden and can
  // float anywhere on screen.
  return ReactDOM.createPortal(node, document.body);
};

export default StatsForNerds;
